/* eslint-disable no-console */
/**
 * explore-new.js
 *
 * Companion script for sections/explore-new.liquid.
 * Keeps the existing AWS API endpoints (calculate-calories, calculate-pricing,
 * store-customer-order, etc.) so the backend Just Works.
 *
 * Tries hard to stay self-contained — no dependency on the legacy explore.js.
 */
(function () {
  'use strict';

  // ─────────────────────────────────────────────────────────────────────────────
  // CONFIG
  // ─────────────────────────────────────────────────────────────────────────────
  const API_BASE = 'https://isnjz3kdxk.execute-api.us-east-1.amazonaws.com/prod';
  const API_KEY = 'gmkxBXr02VaPXOhdX79Ywa9RkG7TvViO1FGUSd58';
  const DISCOUNT_CODES_URL = 'https://api.bogeysmix.com/public/discount-codes';

  const PLAN_LABELS = {
    complete: 'Complete Meal Plan',
    intro: 'Introductory Meal Plan',
    'medium-topper': 'Half & Half Topper',
    'light-topper': 'Light Topper',
  };

  // Competitor price comparison. Multipliers are derived from each brand's
  // published 14-day cost vs. Bogey's snapshot below, so the bars scale with the
  // user's actual computed price instead of going stale. Update the snapshot
  // (and COMPARE_AS_OF) when you re-survey competitor pricing.
  const COMPARE_AS_OF = 'June 2026';
  const COMPARE_BOGEY_ANCHOR = 36.95; // Bogey's 14-day cost the multipliers were derived from
  const COMPETITORS = [
    { name: "Bogey's Mix", cost: 36.95, you: true },
    { name: 'Freshpet', cost: 38.70 },
    { name: 'JustFoodForDogs', cost: 41.40 },
    { name: "The Farmer's Dog", cost: 53.60 },
    { name: 'Nom Nom', cost: 57.60 },
    { name: 'Ollie', cost: 59.10 },
  ].map((c) => ({ ...c, mult: c.cost / 36.95 }));

  // Selling plan IDs (kept in sync with legacy explore.js)
  const DEFAULT_SELLING_PLANS = { complete: '691945734446', light: '692565672238', medium: '692565573934' };
  const SELLING_PLANS_BY_DISCOUNT = {
    50: { complete: '692062880046', light: '692565705006', medium: '692565606702' },
    60: { complete: '692440957230', light: '692565737774', medium: '692565639470' },
    100: { complete: '692568785198', light: '692568719662', medium: '692568752430' },
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const state = {
    step: 0,
    xp: 0,
    pup: {
      name: '',
      weight: 40,
      age: 3,
      activity: '',
      fixed: '',
      treats: 2,
    },
    calories: null, // base calories (before treats)
    caloriesWithTreats: null,
    adjustedCalories: null, // user-overridden via slider
    recipe: 'turkey',
    selectedPlan: 'intro', // default to the Intro (recommended start) plan
    planType: 'complete', // 'complete' | 'topper'
    pricing: null,
    discount: { code: '', percent: 0, applied: false },
    activeDiscountCodes: null,
    address: { line1: '', apt: '', zip: '', email: '', phone: '', name: '', smsOptIn: false, password: '' },
    extras: { breed: '', gender: '', hearAboutUs: '', photoUrl: null },
    shopifyStatus: null,
    skuInfo: null,
    expectedDelivery: null,
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // DOM HELPERS
  // ─────────────────────────────────────────────────────────────────────────────
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  function debounce(fn, wait = 300) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // FORM PERSISTENCE — save-as-you-type to localStorage so a refresh keeps info
  // (mirrors the legacy explore page). Password is intentionally NOT persisted.
  // ─────────────────────────────────────────────────────────────────────────────
  const PERSIST_KEY = 'exploreTestForm';
  const PERSIST_TEXT_IDS = [
    'xnDogName', 'xnDogWeight', 'xnDogAge',
    'xnParentName', 'xnParentAddress', 'xnParentApt', 'xnParentEmail', 'xnParentPhone',
    'xnDogBreed', 'xnDiscountInput',
  ];
  const PERSIST_SELECT_IDS = ['xnHearAboutUs'];
  const PERSIST_CHECKBOX_IDS = ['xnSmsOptIn'];
  const PERSIST_RADIO_NAMES = ['activityLevel', 'fixedOrNot', 'treatQuantity', 'dogGender', 'recipe', 'selectedPlan'];

  function collectFormData() {
    const data = { text: {}, select: {}, check: {}, radio: {} };
    PERSIST_TEXT_IDS.forEach((id) => { const el = document.getElementById(id); if (el) data.text[id] = el.value; });
    PERSIST_SELECT_IDS.forEach((id) => { const el = document.getElementById(id); if (el) data.select[id] = el.value; });
    PERSIST_CHECKBOX_IDS.forEach((id) => { const el = document.getElementById(id); if (el) data.check[id] = !!el.checked; });
    PERSIST_RADIO_NAMES.forEach((name) => {
      const sel = document.querySelector(`input[name="${name}"]:checked`);
      if (sel) data.radio[name] = sel.value;
    });
    return data;
  }

  const persistForm = debounce(() => {
    try { localStorage.setItem(PERSIST_KEY, JSON.stringify(collectFormData())); } catch (e) { /* quota / private mode */ }
  }, 400);

  function clearPersistedForm() {
    try { localStorage.removeItem(PERSIST_KEY); } catch (e) { /* noop */ }
  }

  function restorePersistedForm() {
    let data;
    try { data = JSON.parse(localStorage.getItem(PERSIST_KEY) || 'null'); } catch (e) { data = null; }
    if (!data) return;

    // Text + range inputs (dispatch 'input' so existing handlers update state + displays)
    Object.entries(data.text || {}).forEach(([id, val]) => {
      if (val == null || val === '') return;
      const el = document.getElementById(id);
      if (!el) return;
      el.value = val;
      if (id === 'xnDogBreed') { state.extras.breed = val; return; } // skip dispatch to avoid opening the breed dropdown
      el.dispatchEvent(new Event('input', { bubbles: true }));
    });
    Object.entries(data.select || {}).forEach(([id, val]) => {
      const el = document.getElementById(id);
      if (el && val) { el.value = val; el.dispatchEvent(new Event('change', { bubbles: true })); }
    });
    Object.entries(data.check || {}).forEach(([id, val]) => {
      const el = document.getElementById(id);
      if (el) { el.checked = !!val; el.dispatchEvent(new Event('change', { bubbles: true })); }
    });
    Object.entries(data.radio || {}).forEach(([name, val]) => {
      const el = document.querySelector(`input[name="${name}"][value="${val}"]`);
      if (el) { el.checked = true; el.dispatchEvent(new Event('change', { bubbles: true })); }
    });

    // Restore the plan-type tab + selection so the right cards show on refresh.
    const savedPlan = (data.radio || {}).selectedPlan;
    if (savedPlan) {
      const type = (savedPlan === 'medium-topper' || savedPlan === 'light-topper') ? 'topper' : 'complete';
      try { setPlanTypeView(type, savedPlan); } catch (e) { /* noop */ }
    }

    // Close any autocomplete menu that may have popped open, then re-validate step 1
    document.querySelectorAll('.xn-autocomplete__menu.is-open').forEach((m) => m.classList.remove('is-open'));
    try { validateStep1(); } catch (e) { /* noop */ }
  }

  function initFormPersistence() {
    const root = document.getElementById('explore-new-root');
    if (!root) return;
    // Capture phase so we record values even when inner handlers stopPropagation
    root.addEventListener('input', persistForm, true);
    root.addEventListener('change', persistForm, true);
  }

  function showToast(msg, opts = {}) {
    const el = $('#xnToast');
    if (!el) return;
    el.querySelector('.xn-toast__msg').textContent = msg;
    el.hidden = false;
    el.classList.remove('is-out');
    el.classList.add('is-in');
    clearTimeout(showToast._t);
    showToast._t = setTimeout(() => {
      el.classList.remove('is-in');
      el.classList.add('is-out');
      setTimeout(() => { el.hidden = true; el.classList.remove('is-out'); }, 400);
    }, opts.duration || 2200);
  }

  function refreshIcons() {
    if (window.lucide && typeof window.lucide.createIcons === 'function') {
      try { window.lucide.createIcons(); } catch (e) { /* noop */ }
    }
  }

  function scrollToStep(step, behavior = 'smooth') {
    const el = $(`#xnStep${step}`);
    if (!el) return;
    const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    const xpH = $('.xn-progress')?.offsetHeight || 0;
    const top = el.getBoundingClientRect().top + window.pageYOffset - headerH - xpH - 16;
    window.scrollTo({ top: Math.max(0, top), behavior });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // XP / PROGRESS
  // ─────────────────────────────────────────────────────────────────────────────
  function setStep(step) {
    state.step = step;
    const total = 3;
    const pct = ((step - 1) / total) * 100;
    const fill = $('#xnProgressFill');
    if (fill) fill.style.width = pct + '%';

    $$('.xn-progress__step').forEach((b) => {
      const s = parseInt(b.dataset.stepJump, 10);
      b.classList.toggle('is-active', s === step);
      b.classList.toggle('is-done', s < step);
    });
    $('#xnProgress')?.setAttribute('data-current-step', String(step));

    // Unlock subsequent sections (allow user to scroll/see them once unlocked)
    [1, 2, 3].forEach((s) => {
      const el = $(`#xnStep${s}`);
      if (!el) return;
      el.classList.toggle('is-locked', s > step);
    });
  }

  function addXp(amount, reason) {
    state.xp += amount;
    const el = $('#xnXpValue');
    if (el) animateNumber(el, state.xp, 500);
    if (reason) showToast(`+${amount} XP · ${reason}`);
    pulseXp();
  }

  function pulseXp() {
    const xp = $('.xn-progress__xp');
    if (!xp) return;
    xp.classList.remove('is-pop');
    void xp.offsetWidth; // restart animation
    xp.classList.add('is-pop');
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // NUMBER ANIMATION
  // ─────────────────────────────────────────────────────────────────────────────
  function animateNumber(el, target, duration = 700) {
    if (!el) return;
    const start = parseFloat((el.textContent || '0').replace(/[^\d.-]/g, '')) || 0;
    const end = Number(target) || 0;
    const t0 = performance.now();
    function frame(now) {
      const t = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const val = Math.round(start + (end - start) * eased);
      el.textContent = String(val);
      if (t < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // CONFETTI (lightweight, dependency-free)
  // ─────────────────────────────────────────────────────────────────────────────
  function fireConfetti(canvas) {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width = canvas.offsetWidth;
    const h = canvas.height = canvas.offsetHeight;
    const colors = ['#fbf837', '#F4F1B5', '#B3DDF6', '#fffeed', '#002A32'];
    const pieces = [];
    for (let i = 0; i < 120; i++) {
      pieces.push({
        x: w / 2 + (Math.random() - 0.5) * 80,
        y: h / 2,
        vx: (Math.random() - 0.5) * 8,
        vy: -Math.random() * 8 - 4,
        g: 0.25 + Math.random() * 0.15,
        s: 4 + Math.random() * 4,
        r: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.3,
        c: colors[Math.floor(Math.random() * colors.length)],
        a: 1,
      });
    }
    let frames = 0;
    function tick() {
      ctx.clearRect(0, 0, w, h);
      pieces.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += p.g;
        p.r += p.vr;
        p.a -= 0.005;
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.a);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.r);
        ctx.fillStyle = p.c;
        ctx.fillRect(-p.s / 2, -p.s / 2, p.s, p.s * 0.6);
        ctx.restore();
      });
      frames++;
      if (frames < 180) requestAnimationFrame(tick);
      else ctx.clearRect(0, 0, w, h);
    }
    tick();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 1: PUP FORM
  // ─────────────────────────────────────────────────────────────────────────────
  function initStep1() {
    const form = $('#xnPupForm');
    if (!form) return;

    // Dog name
    const nameEl = $('#xnDogName');
    nameEl?.addEventListener('input', (e) => {
      state.pup.name = e.target.value.trim();
      validateStep1();
    });
    // Trim the field value itself on blur so the displayed + persisted value is
    // clean (not just state), and collapse any internal double-spaces.
    nameEl?.addEventListener('blur', (e) => {
      const cleaned = e.target.value.trim().replace(/\s+/g, ' ');
      e.target.value = cleaned;
      state.pup.name = cleaned;
    });

    // Weight slider
    const weightEl = $('#xnDogWeight');
    const weightDisp = $('#xnDogWeightDisplay');
    function updWeight(v) {
      state.pup.weight = parseFloat(v);
      if (weightDisp) weightDisp.textContent = String(parseFloat(v));
      highlightSizeMarker(parseFloat(v));
      paintSliderFill(weightEl);
    }
    weightEl?.addEventListener('input', (e) => { updWeight(e.target.value); validateStep1(); });
    if (weightEl) updWeight(weightEl.value);

    // Age slider
    const ageEl = $('#xnDogAge');
    const ageDisp = $('#xnDogAgeDisplay');
    const ageUnit = $('#xnDogAgeUnit');
    const lifeStage = $('#xnLifeStage');
    function updAge(v) {
      const n = parseFloat(v);
      state.pup.age = n;
      if (ageDisp) ageDisp.textContent = n % 1 === 0 ? String(n) : n.toFixed(1);
      if (ageUnit) ageUnit.textContent = n < 1 ? 'month' + (n * 12 === 1 ? '' : 's') : 'year' + (n === 1 ? '' : 's');
      if (n < 1) ageUnit && (ageUnit.textContent = (n * 12) + ' months');
      if (lifeStage) lifeStage.textContent = n < 1 ? '🐶 Puppy (too young for full plan)' : n < 7 ? '🐕 Adult' : '🐾 Senior';
      paintSliderFill(ageEl);
    }
    ageEl?.addEventListener('input', (e) => { updAge(e.target.value); validateStep1(); });
    if (ageEl) updAge(ageEl.value);

    // Radio groups
    ['activityLevel', 'fixedOrNot', 'treatQuantity'].forEach((name) => {
      $$(`input[name="${name}"]`, form).forEach((inp) => {
        inp.addEventListener('change', () => {
          if (name === 'activityLevel') state.pup.activity = inp.value;
          if (name === 'fixedOrNot') state.pup.fixed = inp.value;
          if (name === 'treatQuantity') state.pup.treats = parseFloat(inp.value);
          validateStep1();
        });
      });
    });

    // How'd you hear about us (required, captured here near the calorie calc)
    $('#xnHearAboutUs')?.addEventListener('change', (e) => {
      state.extras.hearAboutUs = e.target.value || '';
      validateStep1();
    });

    // Form submit
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!validateStep1(true)) return;
      await unlockPlan();
    });
  }

  function highlightSizeMarker(weight) {
    const markers = $$('.xn-size-marker');
    markers.forEach((m) => {
      const w = parseFloat(m.dataset.w);
      const active = weight >= w;
      m.classList.toggle('is-active', active);
    });
  }

  function paintSliderFill(slider) {
    if (!slider) return;
    const min = parseFloat(slider.min) || 0;
    const max = parseFloat(slider.max) || 100;
    const val = parseFloat(slider.value);
    const pct = ((val - min) / (max - min)) * 100;
    slider.style.setProperty('--slider-fill', pct + '%');
  }

  function validateStep1(showErrors = false) {
    const p = state.pup;
    const checks = [
      { ok: !!p.name, field: 'dogName' },
      { ok: p.weight > 0, field: 'dogWeight' },
      { ok: p.age > 0, field: 'dogAge' },
      { ok: !!p.activity, field: 'activityLevel' },
      { ok: !!p.fixed, field: 'fixedOrNot' },
      { ok: !isNaN(p.treats), field: 'treatQuantity' },
      { ok: !!state.extras.hearAboutUs, field: 'hearAboutUs' },
    ];
    const allOk = checks.every((c) => c.ok);

    if (showErrors) {
      // Flag every incomplete field so the user can see them all at once.
      checks.forEach((c) => {
        const fEl = $(`[data-field="${c.field}"]`);
        if (fEl) fEl.classList.toggle('is-error', !c.ok);
      });
      const missing = checks.filter((c) => !c.ok);
      const firstBad = missing[0];
      if (firstBad) {
        const fEl = $(`[data-field="${firstBad.field}"]`);
        fEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Briefly draw attention to the first missing field.
        fEl?.classList.remove('xn-field--shake');
        // eslint-disable-next-line no-unused-expressions
        fEl?.offsetWidth; // force reflow so the animation can replay
        fEl?.classList.add('xn-field--shake');
        const msg = missing.length === 1
          ? 'Just one more field to fill in.'
          : `Please fill in ${missing.length} more fields to continue.`;
        showToast(msg, { duration: 3500 });
      }
    } else {
      // Light: clear errors as user fills fields in
      checks.forEach((c) => {
        const fEl = $(`[data-field="${c.field}"]`);
        if (fEl && c.ok) fEl.classList.remove('is-error');
      });
    }

    // Keep the button clickable so users get feedback; just flag the "not ready" state.
    const submitBtn = $('#xnStep1Submit');
    if (submitBtn) submitBtn.classList.toggle('is-incomplete', !allOk);
    return allOk;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // UNLOCK PLAN — calls calculate-calories + calculate-pricing
  // ─────────────────────────────────────────────────────────────────────────────
  async function unlockPlan() {
    const btn = $('#xnStep1Submit');
    if (btn) { btn.disabled = true; btn.classList.add('is-loading'); btn.querySelector('.xn-btn__label').textContent = 'Crunching numbers…'; }

    try {
      // 1) Calories
      const caloriesRes = await fetch(`${API_BASE}/calculate-calories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({
          weight_lbs: state.pup.weight,
          activity_level: state.pup.activity,
          treat_quantity: state.pup.treats,
          dog_name: state.pup.name,
          dog_age: state.pup.age,
          fixed_or_not: state.pup.fixed,
          hear_about_us: state.extras.hearAboutUs || '',
        }),
      });
      const caloriesJson = await caloriesRes.json();
      const caloriesBody = typeof caloriesJson.body === 'string' ? JSON.parse(caloriesJson.body) : caloriesJson;

      if (!caloriesRes.ok || !caloriesBody.calories) {
        throw new Error(caloriesBody.message || 'Could not calculate calories.');
      }

      state.calories = parseInt(caloriesBody.calories, 10);
      state.caloriesWithTreats = parseInt(caloriesBody.total_calories_with_treats || caloriesBody.calories, 10);
      state.adjustedCalories = state.caloriesWithTreats;

      // 2) Pricing (uses calories-with-treats from step above)
      await fetchPricing(state.caloriesWithTreats);

      // Move to step 2 + reveal
      setStep(2);
      revealStep2();
      addXp(50, "Pup profile complete!");
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Something went sideways. Try again.', { duration: 3500 });
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.classList.remove('is-loading');
        btn.querySelector('.xn-btn__label').textContent = 'Unlock my plan';
      }
    }
  }

  // Calls the SAME calculate-calories lambda as the legacy explore page, with the full
  // contact payload, so the calorie/lead email fires identically. Best-effort: never blocks
  // or breaks the activation flow.
  async function triggerCalorieEmailLambda() {
    try {
      const fullAddress = [state.address.line1, state.address.apt].filter(Boolean).join(' ');
      await fetch(`${API_BASE}/calculate-calories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({
          weight_lbs: parseFloat(state.pup.weight),
          activity_level: state.pup.activity,
          treat_quantity: state.pup.treats,
          dog_name: state.pup.name || '',
          dog_breed: state.extras.breed || '',
          dog_age: state.pup.age,
          dog_sex: state.extras.gender || '',
          parent_name: state.address.name || '',
          parent_email: (state.address.email || '').toLowerCase(),
          parent_phone_number: state.address.phone || '',
          parent_address: fullAddress,
          zip_code: state.address.zip || '',
          discount_code: state.discount.code || '',
          hear_about_us: state.extras.hearAboutUs || '',
          fixed_or_not: state.pup.fixed,
        }),
      });
    } catch (e) {
      console.error('calorie email lambda (non-blocking) failed:', e);
    }
  }

  async function fetchPricing(caloriesForOrder) {
    const res = await fetch(`${API_BASE}/calculate-pricing`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
      body: JSON.stringify({
        weight_lbs: state.pup.weight,
        activity_level: state.pup.activity,
        treat_quantity: state.pup.treats,
        meal_plan_calories: caloriesForOrder,
        recipe: state.recipe,
      }),
    });
    const json = await res.json();
    const body = typeof json.body === 'string' ? JSON.parse(json.body) : json;
    if (!res.ok || !body.pricing_options) throw new Error(body.message || 'Could not fetch pricing.');
    state.pricing = body.pricing_options;
    renderPricing();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 2: REVEAL
  // ─────────────────────────────────────────────────────────────────────────────
  function revealStep2() {
    const step2 = $('#xnStep2');
    if (!step2) return;
    step2.classList.remove('is-locked');
    step2.classList.add('is-revealed');

    const nameEl = $('#xnRevealDogName');
    if (nameEl) nameEl.textContent = state.pup.name || 'Your pup';

    const calsEl = $('#xnRevealCalories');
    if (calsEl) animateNumber(calsEl, state.caloriesWithTreats, 1100);

    // Calorie slider initialize
    const slider = $('#xnCalorieAdjuster');
    if (slider) {
      slider.value = String(state.caloriesWithTreats);
      $('#xnCalorieAdjusterDisplay').textContent = String(state.caloriesWithTreats);
      $('#xnCalorieRecNote').textContent = `Recommended: ${state.caloriesWithTreats} cal/day`;
      paintSliderFill(slider);
    }

    // Confetti
    setTimeout(() => fireConfetti($('#xnConfetti')), 300);

    // Scroll
    scrollToStep(2);

    // Kick off counter animations + fact carousels
    triggerOnView();
    startFactCarousels();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // PRICING DISPLAY
  // ─────────────────────────────────────────────────────────────────────────────
  function renderPricing() {
    if (!state.pricing) return;
    const p = state.pricing;
    setPrice('xnPriceComplete', 'xnSupplyComplete', p.complete_meal_plan);
    setPrice('xnPriceIntro', 'xnSupplyIntro', p.intro_meal_plan);
    setPrice('xnPriceMedium', 'xnSupplyMedium', p.medium_topper_plan);
    setPrice('xnPriceLight', 'xnSupplyLight', p.light_topper_plan);

    // Too young handling
    const tooYoung = state.pup.age < 1;
    $('#xnTooYoungWarn').hidden = !tooYoung;

    // Show/hide intro plan: only if user marked NOT feeding fresh food today (we don't ask now; show always for complete option, hide intro by default — surface as toggle later)
    $('#xnPlanIntro').hidden = true;

    // Apply discount visually if set
    if (state.discount.applied && state.discount.percent > 0) {
      applyDiscountToPrices(state.discount.percent);
    }

    setPlanTypeView(state.planType);
    renderCompareMatrix();
  }

  function setPrice(dailyId, supplyId, planObj) {
    if (!planObj) return;
    const dailyEl = document.getElementById(dailyId);
    const supplyEl = document.getElementById(supplyId);
    if (dailyEl) dailyEl.dataset.original = String(planObj.daily_price);
    if (supplyEl) supplyEl.dataset.original = String(planObj.two_week_price);
    if (dailyEl) dailyEl.textContent = `$${Number(planObj.daily_price).toFixed(2)}`;
    if (supplyEl) supplyEl.textContent = `$${Number(planObj.two_week_price).toFixed(2)}`;
  }

  // Build the competitor price-comparison bars. Anchors to the user's live
  // complete-plan 14-day price (falls back to the snapshot before pricing loads),
  // so every brand scales proportionally to what Bogey's actually costs them.
  function renderCompareMatrix() {
    const wrap = document.getElementById('xnCompareBars');
    if (!wrap) return;
    const anchor = (state.pricing && state.pricing.complete_meal_plan &&
      Number(state.pricing.complete_meal_plan.two_week_price)) || COMPARE_BOGEY_ANCHOR;
    const rows = COMPETITORS.map((c) => ({ name: c.name, you: !!c.you, cost: anchor * c.mult }));
    const max = Math.max.apply(null, rows.map((r) => r.cost));
    wrap.innerHTML = rows.map((r) => {
      const pct = Math.max(8, Math.round((r.cost / max) * 100));
      const pill = r.you ? ' <span class="xn-compare__pill">Best value</span>' : '';
      return `<div class="xn-compare__row${r.you ? ' is-you' : ''}" style="--w:${pct}%">
        <span class="xn-compare__brand">${r.name}${pill}</span>
        <div class="xn-compare__track"><div class="xn-compare__fill"></div></div>
        <span class="xn-compare__cost">$${r.cost.toFixed(2)}</span>
      </div>`;
    }).join('');

    const you = rows.find((r) => r.you);
    const priciest = rows.reduce((a, b) => (b.cost > a.cost ? b : a));
    const note = document.getElementById('xnCompareNote');
    if (note && you && priciest && priciest.cost > you.cost) {
      const saveMonth = (priciest.cost - you.cost) * (30 / 14);
      note.innerHTML = `You'd save about <strong>$${saveMonth.toFixed(0)}/mo</strong> vs ${priciest.name}. ` +
        `<span class="xn-compare__disc">Estimated 14-day cost; competitor pricing based on published rates as of ${COMPARE_AS_OF}.</span>`;
    }
  }

  function setPlanTypeView(type, preferredPlan) {
    state.planType = type;
    const isComplete = type === 'complete';
    $$('#xnPlanTypeToggle .xn-toggle__opt').forEach((b) => {
      b.classList.toggle('is-active', b.dataset.planType === type);
    });

    $('#xnPlans')?.setAttribute('data-active-type', isComplete ? 'complete' : 'topper');

    // Under "Complete meal" we show BOTH Intro (recommended start) and Complete.
    $('[data-plan="intro"]').hidden = !isComplete;
    $('[data-plan="complete"]').hidden = !isComplete;
    $('[data-plan="medium-topper"]').hidden = isComplete;
    $('[data-plan="light-topper"]').hidden = isComplete;

    // Select a plan: honor a restored preference, otherwise default (Intro / Medium topper).
    const validForType = isComplete ? ['intro', 'complete'] : ['medium-topper', 'light-topper'];
    const sel = (preferredPlan && validForType.includes(preferredPlan))
      ? preferredPlan
      : (isComplete ? 'intro' : 'medium-topper');
    state.selectedPlan = sel;
    const radio = document.querySelector(`input[name="selectedPlan"][value="${sel}"]`);
    if (radio) radio.checked = true;
    updatePlanSelectionUI();
  }

  function updatePlanSelectionUI() {
    $$('.xn-plan').forEach((el) => {
      const planVal = el.dataset.plan;
      el.classList.toggle('is-selected', planVal === state.selectedPlan);
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 2 INTERACTIONS (recipe, plan type, plan select, calorie slider)
  // ─────────────────────────────────────────────────────────────────────────────
  function initStep2() {
    // Plan type toggle
    $$('#xnPlanTypeToggle .xn-toggle__opt').forEach((b) => {
      b.addEventListener('click', () => setPlanTypeView(b.dataset.planType));
    });

    // Topper switch CTA in defense block
    $('#xnSwitchToTopper')?.addEventListener('click', () => {
      setPlanTypeView('topper');
      scrollToEl('#xnPlans');
      showToast('Switched to topper plans');
    });

    // Recipe change → refetch pricing
    $$('input[name="recipe"]').forEach((r) => {
      r.addEventListener('change', async () => {
        state.recipe = r.value;
        $$('.xn-recipe').forEach((el) => el.classList.toggle('is-selected', el.dataset.recipe === r.value));
        if (state.pup.weight && state.pup.activity) {
          try { await fetchPricing(state.adjustedCalories || state.caloriesWithTreats); } catch (e) { console.error(e); }
        }
      });
    });
    // Initial recipe selection class
    $$('.xn-recipe').forEach((el) => el.classList.toggle('is-selected', el.dataset.recipe === state.recipe));

    // Plan selection
    $$('input[name="selectedPlan"]').forEach((r) => {
      r.addEventListener('change', () => {
        state.selectedPlan = r.value;
        updatePlanSelectionUI();
      });
    });

    // Calorie adjuster
    const slider = $('#xnCalorieAdjuster');
    const disp = $('#xnCalorieAdjusterDisplay');
    const refetchPricing = debounce(async () => {
      try { await fetchPricing(state.adjustedCalories); } catch (e) { console.error(e); }
    }, 350);
    slider?.addEventListener('input', (e) => {
      const v = parseInt(e.target.value, 10);
      state.adjustedCalories = v;
      if (disp) disp.textContent = String(v);
      paintSliderFill(slider);
      refetchPricing();
    });

    // Step 2 continue — both the express CTA (next to the plan) and the bottom CTA
    const continueToStep3 = () => {
      if (!state.selectedPlan) { showToast('Pick a plan first 🐶'); return; }
      setStep(3);
      $('#xnStep3').classList.remove('is-locked');
      // Scroll now, then correct once more after layout settles. The express
      // button sits high up and there's a lot of lazy-loaded content between it
      // and Step 3 — those images can load mid-scroll and leave us landing short.
      scrollToStep(3);
      requestAnimationFrame(() => scrollToStep(3));
      setTimeout(() => scrollToStep(3), 450);
      addXp(50, "Plan locked in!");
    };
    $('#xnStep2Continue')?.addEventListener('click', continueToStep3);
    $('#xnStep2ContinueExpress')?.addEventListener('click', continueToStep3);

    // "Keep exploring" — smooth-scroll to the first defense block
    $$('[data-scroll-to]').forEach((btn) => {
      btn.addEventListener('click', () => scrollToEl(btn.dataset.scrollTo));
    });

    // Discount apply
    $('#xnDiscountApply')?.addEventListener('click', () => applyDiscountCode());
    $('#xnDiscountInput')?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); applyDiscountCode(); }
    });

    // Discount tier unlock cards
    $$('.xn-unlock-card').forEach((card) => {
      card.addEventListener('click', () => openUnlockModal(card.dataset.discountTier));
    });

    // Competitor button — open live chat (Shopify Inbox) so they can talk to a
    // real human. Falls back to the lead-capture form if the widget isn't ready.
    $('#xnCompetitorBtn')?.addEventListener('click', () => {
      if (openShopifyInbox()) return;
      let tries = 0;
      const t = setInterval(() => {
        tries += 1;
        if (openShopifyInbox()) { clearInterval(t); }
        else if (tries >= 6) { clearInterval(t); openModal('#xnCompetitorModal'); }
      }, 300);
    });
    $('#xnCompetitorForm')?.addEventListener('submit', submitCompetitor);

    // DMV free taste pack
    $('#xnDmvBtn')?.addEventListener('click', () => { openModal('#xnDmvModal'); setupDmvAutocomplete(); });
    $('#xnDmvForm')?.addEventListener('submit', submitDmvTastePack);

    // Unlock modal form
    $('#xnUnlockForm')?.addEventListener('submit', submitUnlockForm);

    // Modal close
    $$('[data-close-modal]').forEach((el) => el.addEventListener('click', closeAllModals));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAllModals(); });
  }

  function scrollToEl(sel) {
    const el = $(sel);
    if (!el) return;
    const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    const xpH = $('.xn-progress')?.offsetHeight || 0;
    const top = el.getBoundingClientRect().top + window.pageYOffset - headerH - xpH - 16;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // DISCOUNT CODES
  // ─────────────────────────────────────────────────────────────────────────────
  async function fetchActiveDiscountCodes() {
    if (state.activeDiscountCodes) return state.activeDiscountCodes;
    try {
      const res = await fetch(DISCOUNT_CODES_URL);
      const data = await res.json();
      state.activeDiscountCodes = Array.isArray(data) ? data : (data && data.codes) || [];
    } catch (e) {
      console.error('discount codes fetch failed', e);
      state.activeDiscountCodes = [];
    }
    return state.activeDiscountCodes;
  }

  async function applyDiscountCode() {
    const input = $('#xnDiscountInput');
    const status = $('#xnDiscountStatus');
    const raw = (input?.value || '').trim().toUpperCase();
    if (!raw) {
      if (status) { status.hidden = false; status.textContent = 'Enter a code first.'; status.className = 'xn-discount__status is-error'; }
      return;
    }
    const codes = await fetchActiveDiscountCodes();
    const match = codes.find((c) => (c.code || c).toString().toUpperCase() === raw);
    if (!match) {
      if (status) { status.hidden = false; status.textContent = 'Invalid code 🐾'; status.className = 'xn-discount__status is-error'; }
      state.discount = { code: '', percent: 0, applied: false };
      renderPricing();
      return;
    }
    const pct = parseFloat(match.discount_percentage || match.percent || 0);
    state.discount = { code: raw, percent: pct, applied: true };
    if (status) {
      status.hidden = false;
      status.innerHTML = `<i data-lucide="circle-check"></i> <strong>${raw}</strong> applied — ${pct}% off your first order!`;
      status.className = 'xn-discount__status is-success';
      refreshIcons();
    }
    applyDiscountToPrices(pct);
    addXp(25, `Code applied — ${pct}% off`);
  }

  function applyDiscountToPrices(pct) {
    if (!pct || pct <= 0) return;
    const ids = ['xnPriceComplete', 'xnPriceIntro', 'xnPriceMedium', 'xnPriceLight'];
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const orig = parseFloat(el.dataset.original || el.textContent.replace(/[^\d.]/g, '')) || 0;
      const discounted = orig * (100 - pct) / 100;
      el.innerHTML = `<span class="xn-price-strike">$${orig.toFixed(2)}</span> $${discounted.toFixed(2)}`;
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // MODALS
  // ─────────────────────────────────────────────────────────────────────────────
  function openModal(sel) {
    const m = $(sel);
    if (!m) return;
    m.hidden = false;
    document.body.classList.add('xn-no-scroll');
    requestAnimationFrame(() => m.classList.add('is-open'));
    refreshIcons();
  }
  function closeAllModals() {
    $$('.xn-modal').forEach((m) => {
      m.classList.remove('is-open');
      setTimeout(() => { m.hidden = true; }, 200);
    });
    document.body.classList.remove('xn-no-scroll');
  }

  function openUnlockModal(tier) {
    const pct = parseInt(tier, 10);
    $('#xnUnlockPct').textContent = String(pct);
    const isPhone = pct >= 60;
    $('#xnUnlockEmailField').hidden = isPhone;
    $('#xnUnlockPhoneField').hidden = !isPhone;
    $('#xnUnlockSub').textContent = isPhone
      ? 'Enter your number — your 60% code applies instantly, and we\'ll text you a copy.'
      : 'Enter your email — your 50% code applies instantly, and we\'ll email you a copy.';
    // Toggle the SMS consent disclosure with the phone field.
    const consent = $('#xnUnlockSmsConsent');
    if (consent) consent.hidden = !isPhone;
    openModal('#xnUnlockModal');
  }

  async function submitUnlockForm(e) {
    e.preventDefault();
    const pct = parseInt($('#xnUnlockPct').textContent, 10);
    const isPhone = pct >= 60;
    const email = $('#xnUnlockEmail').value.trim();
    const phone = $('#xnUnlockPhone').value.trim();
    if (isPhone && !/^\d{10}$/.test(phone)) { showToast('10-digit phone please'); return; }
    if (!isPhone && !/^[^@]+@[^@]+\.[^@]+$/.test(email)) { showToast('Valid email please'); return; }

    const code = isPhone ? 'WOOF60' : 'WOOF50';

    // 1) Reveal + apply the code immediately — no waiting on a message to arrive.
    $('#xnDiscountInput').value = code;
    try { await applyDiscountCode(); } catch (err) { console.error(err); }
    closeAllModals();
    showToast(`Code ${code} applied! We'll ${isPhone ? 'text' : 'email'} you a copy too.`, { duration: 3500 });

    // 2) Save the lead and ask the backend to fire off the confirmation message.
    try {
      await fetch(`${API_BASE}/store-discount-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({
          email: email || null,
          phone_number: phone || null,
          discount_percentage: pct,
          discount_code: code,
          dog_name: state.pup.name || null,
          source: 'explore-new',
          send_confirmation: true,
        }),
      });
    } catch (err) {
      console.error('store-discount-signup failed (code already applied):', err);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // DOG AVATAR PHOTO UPLOAD (optional extras)
  // ─────────────────────────────────────────────────────────────────────────────
  function initDogPhotoUpload() {
    const btn = document.getElementById('xnDogPhotoBtn');
    const input = document.getElementById('xnDogPhotoInput');
    const removeBtn = document.getElementById('xnDogPhotoRemove');
    if (!btn || !input) return;
    btn.addEventListener('click', () => input.click());
    input.addEventListener('change', handleDogPhotoSelect);
    removeBtn?.addEventListener('click', clearDogPhoto);

    // Restore a previously uploaded photo (persisted across refreshes).
    try {
      const saved = localStorage.getItem('dogPhotoUrl');
      if (saved) {
        state.extras.photoUrl = saved;
        showDogPhotoPreview(saved);
        removeBtn?.removeAttribute('hidden');
      }
    } catch (e) { /* storage unavailable */ }
  }

  function setDogPhotoStatus(msg) {
    const el = document.getElementById('xnDogPhotoStatus');
    if (el) el.textContent = msg || '';
  }

  function showDogPhotoPreview(src) {
    const prev = document.getElementById('xnDogPhotoPreview');
    if (!prev) return;
    prev.innerHTML = `<img src="${src}" alt="Your dog">`;
    prev.classList.add('has-photo');
  }

  function clearDogPhoto() {
    state.extras.photoUrl = null;
    try { localStorage.removeItem('dogPhotoUrl'); } catch (e) { /* noop */ }
    const prev = document.getElementById('xnDogPhotoPreview');
    if (prev) { prev.innerHTML = '<i data-lucide="dog"></i>'; prev.classList.remove('has-photo'); refreshIcons(); }
    const input = document.getElementById('xnDogPhotoInput');
    if (input) input.value = '';
    document.getElementById('xnDogPhotoRemove')?.setAttribute('hidden', '');
    setDogPhotoStatus('');
  }

  async function handleDogPhotoSelect(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) { setDogPhotoStatus('Please choose an image file.'); return; }
    setDogPhotoStatus('Optimizing…');
    try {
      // Downscale + compress client-side so uploads are small and fast.
      const dataUrl = await downscaleImage(file, 800, 0.85);
      showDogPhotoPreview(dataUrl);
      setDogPhotoStatus('Uploading…');
      const url = await uploadDogPhoto(dataUrl);
      state.extras.photoUrl = url;
      try { localStorage.setItem('dogPhotoUrl', url); } catch (e2) { /* noop */ }
      setDogPhotoStatus('Saved ✓');
      document.getElementById('xnDogPhotoRemove')?.removeAttribute('hidden');
    } catch (err) {
      console.error('Dog photo upload failed:', err);
      setDogPhotoStatus('Upload failed — try again');
    }
  }

  // Resize an image File to fit within maxDim (longest side) and return a JPEG data URL.
  function downscaleImage(file, maxDim, quality) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const img = new Image();
      reader.onerror = reject;
      reader.onload = () => { img.src = reader.result; };
      img.onerror = reject;
      img.onload = () => {
        let w = img.width, h = img.height;
        if (w > h && w > maxDim) { h = Math.round(h * maxDim / w); w = maxDim; }
        else if (h >= w && h > maxDim) { w = Math.round(w * maxDim / h); h = maxDim; }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        try { resolve(canvas.toDataURL('image/jpeg', quality)); }
        catch (err) { reject(err); }
      };
      reader.readAsDataURL(file);
    });
  }

  async function uploadDogPhoto(dataUrl) {
    const res = await fetch(`${API_BASE}/upload-dog-photo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
      body: JSON.stringify({
        image_base64: dataUrl, // full data URL: "data:image/jpeg;base64,...."
        content_type: 'image/jpeg',
        dog_name: state.pup.name || '',
        email: (state.address.email || '').toLowerCase(),
      }),
    });
    const json = await res.json();
    const body = typeof json.body === 'string' ? JSON.parse(json.body) : json;
    if (!res.ok || !body.url) throw new Error((body && body.message) || 'Upload failed');
    return body.url;
  }

  // Open the Shopify Inbox chat widget. Modern Inbox is a <#ShopifyChat> web
  // component (shadow DOM) with a <button> launcher inside .chat-app; older
  // builds used an iframe. Returns true if a launcher was found + clicked.
  function openShopifyInbox() {
    try {
      const host = document.getElementById('ShopifyChat');
      const root = host && host.shadowRoot;
      const btn = root && (
        root.querySelector('.chat-app > button') ||
        root.querySelector('button[aria-controls="chat-ui"]') ||
        root.querySelector('button')
      );
      if (btn) { btn.click(); return true; }
    } catch (e) { /* shadow root not ready */ }
    // Legacy iframe fallback
    try {
      const iframe = document.querySelector('#shopify-chat > iframe, #dummy-chat-button-iframe');
      const inner = iframe && iframe.contentWindow &&
        iframe.contentWindow.document.querySelector('button.chat-toggle, button');
      if (inner) { inner.click(); return true; }
    } catch (e) { /* cross-origin or not present */ }
    return false;
  }

  async function submitCompetitor(e) {
    e.preventDefault();
    const brand = $('#xnCompBrand').value.trim();
    const phone = $('#xnCompPhone').value.trim();
    const notes = $('#xnCompNotes').value.trim();
    if (!brand || !/^\d{10}$/.test(phone)) { showToast('Brand + 10-digit phone needed'); return; }
    try {
      await fetch(`${API_BASE}/crm/master-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({
          phone_number: phone,
          tags: ['explore-new', 'competitor-compare', `compare:${brand}`],
          notes: `Wants to compare with ${brand}. ${notes}`,
          source: 'explore-new-competitor',
        }),
      });
      closeAllModals();
      showToast("Got it! We'll text you a side-by-side soon.", { duration: 3500 });
    } catch (err) {
      console.error(err);
      showToast('Could not send — try again');
    }
  }

  // Google Places autocomplete for the DMV taste-pack address field.
  function setupDmvAutocomplete() {
    const input = $('#xnDmvAddress');
    if (!input || input.dataset.acInit === 'true') return;
    if (!(window.google && window.google.maps && window.google.maps.places)) {
      setTimeout(setupDmvAutocomplete, 500);
      return;
    }
    try {
      const ac = new window.google.maps.places.Autocomplete(input, {
        types: ['address'],
        componentRestrictions: { country: 'us' },
        fields: ['address_components', 'formatted_address'],
      });
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (!place || !place.address_components) return;
        input.value = place.formatted_address || input.value;
        const zip = place.address_components.find((c) => c.types.includes('postal_code'));
        if (zip) state.address.zip = zip.short_name;
      });
      input.dataset.acInit = 'true';
    } catch (e) { console.error('DMV autocomplete failed:', e); }
  }

  async function submitDmvTastePack(e) {
    e.preventDefault();
    const name = ($('#xnDmvName').value || '').trim();
    const email = ($('#xnDmvEmail').value || '').trim();
    const address = ($('#xnDmvAddress').value || '').trim();
    if (!name || !/^[^@]+@[^@]+\.[^@]+$/.test(email) || address.length < 5) {
      showToast('Add your name, email & address');
      return;
    }

    // Pre-fill the rest of the flow so they don't re-type if they continue to a plan.
    state.address.name = name;
    state.address.email = email;
    state.address.line1 = address;
    const nameEl = $('#xnParentName'); if (nameEl && !nameEl.value) nameEl.value = name;
    const emailEl = $('#xnParentEmail'); if (emailEl && !emailEl.value) emailEl.value = email;
    const addrEl = $('#xnParentAddress'); if (addrEl && !addrEl.value) addrEl.value = address;
    try {
      localStorage.setItem('parentName', name);
      localStorage.setItem('accountEmail', email);
      localStorage.setItem('parentAddress', address);
    } catch (e2) { /* storage unavailable */ }

    const dogName = state.pup.name || 'your pup';

    // 1) Log the lead/order record (same endpoint the Shop Topper Packs page uses).
    try {
      await fetch(`${API_BASE}/store-topper-pack-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({
          email: email.toLowerCase(),
          dog_name: state.pup.name || '',
          parent_name: name,
          address: address,
          zip_code: state.address.zip || '',
          quantity: 1,
          product: 'DMV Free Taste Pack',
          price: 0,
        }),
      });
    } catch (err) {
      console.error('DMV taste pack request failed:', err);
    }

    // 2) Send the branded "Taste Pack Request" email proposing drop-off dates.
    try {
      await fetch(`${API_BASE}/taste-pack-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({
          customer_email: email.toLowerCase(),
          parent_name: name,
          dog_name: dogName,
          delivery_address: address,
          potential_dates: proposeDropOffDates(),
          dry_run: false,
        }),
      });
    } catch (err) {
      console.error('Taste pack email failed:', err);
    }

    closeAllModals();
    showToast("Yes! Check your email — we'll propose a drop-off date for your free taste pack.", { duration: 4200 });
    try { $('#xnDmvForm').reset(); } catch (e3) { /* noop */ }
    try { addXp(20, 'Taste pack requested!'); } catch (e4) { /* noop */ }
  }

  // Propose the next couple of local drop-off dates (skips weekends), as YYYY-MM-DD.
  function proposeDropOffDates() {
    const fmt = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const dates = [];
    const cursor = new Date();
    cursor.setDate(cursor.getDate() + 2); // start at least 2 days out for prep/routing
    while (dates.length < 2) {
      const dow = cursor.getDay();
      if (dow !== 0 && dow !== 6) dates.push(fmt(cursor)); // skip Sun/Sat
      cursor.setDate(cursor.getDate() + 1);
    }
    return dates;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // FACT CAROUSELS / COUNTERS
  // ─────────────────────────────────────────────────────────────────────────────
  function startFactCarousels() {
    const carousel = $('#xnFactCarousel1');
    if (!carousel) return;
    const facts = $$('.xn-fact', carousel);
    if (facts.length < 2) return;

    // Dots
    const dotsEl = $('#xnFactDots1');
    if (dotsEl && !dotsEl.children.length) {
      facts.forEach((_, i) => {
        const d = document.createElement('button');
        d.type = 'button';
        d.className = 'xn-fact-dot' + (i === 0 ? ' is-active' : '');
        d.addEventListener('click', () => showFact(i));
        dotsEl.appendChild(d);
      });
    }

    let idx = 0;
    function showFact(i) {
      idx = (i + facts.length) % facts.length;
      facts.forEach((f, k) => f.classList.toggle('is-active', k === idx));
      $$('.xn-fact-dot', dotsEl).forEach((d, k) => d.classList.toggle('is-active', k === idx));
    }
    if (startFactCarousels._t) clearInterval(startFactCarousels._t);
    startFactCarousels._t = setInterval(() => showFact(idx + 1), 5200);
  }

  // Reveal-on-scroll: add .is-inview when an element scrolls into view so the
  // CSS transition runs at the right moment (not on page load while off-screen).
  function initScrollReveals() {
    const items = $$('.xn-result-card, .xn-compare, .xn-review-card, .xn-sourcing__item');
    if (!items.length) return;
    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-inview'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-inview');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.25 });
    items.forEach((el) => io.observe(el));
  }

  function triggerOnView() {
    const counters = $$('.xn-counter');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const el = entry.target;
          const target = parseInt(el.dataset.counterTarget, 10) || 0;
          animateNumber(el, target, 1200);
          io.unobserve(el);
        }
      });
    }, { threshold: 0.4 });
    counters.forEach((c) => io.observe(c));
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 3: ACTIVATE
  // ─────────────────────────────────────────────────────────────────────────────
  function initStep3() {
    const form = $('#xnActivateForm');
    if (!form) return;

    // Password toggle
    $('#xnTogglePassword')?.addEventListener('click', () => {
      const inp = $('#xnParentPassword');
      const icon = $('#xnTogglePassword i');
      if (!inp) return;
      const isPwd = inp.type === 'password';
      inp.type = isPwd ? 'text' : 'password';
      if (icon) icon.setAttribute('data-lucide', isPwd ? 'eye-off' : 'eye');
      refreshIcons();
    });

    // Breed autocomplete
    setupBreedAutocomplete();

    // Google Maps autocomplete (if available)
    setupAddressAutocomplete();

    // Live phone formatting
    $('#xnParentPhone')?.addEventListener('input', (e) => {
      e.target.value = (e.target.value || '').replace(/\D/g, '').slice(0, 10);
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      await submitActivate();
    });
  }

  function setupBreedAutocomplete() {
    const input = $('#xnDogBreed');
    const menu = $('#xnDogBreedMenu');
    if (!input || !menu) return;

    let breeds = [];
    fetch(`${API_BASE}/dog-breeds`)
      .then((r) => r.json())
      .then((data) => {
        const body = typeof data.body === 'string' ? JSON.parse(data.body) : data;
        breeds = body.breeds || body || [];
      })
      .catch(() => { breeds = []; });

    function renderMenu(query) {
      menu.innerHTML = '';
      if (!query || !breeds.length) { menu.classList.remove('is-open'); return; }
      const matches = breeds
        .filter((b) => (b.name || b).toString().toLowerCase().includes(query.toLowerCase()))
        .slice(0, 8);
      if (!matches.length) { menu.classList.remove('is-open'); return; }
      matches.forEach((m) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'xn-autocomplete__item';
        item.textContent = m.name || m;
        item.addEventListener('mousedown', (e) => {
          e.preventDefault();
          input.value = m.name || m;
          state.extras.breed = input.value;
          menu.classList.remove('is-open');
        });
        menu.appendChild(item);
      });
      menu.classList.add('is-open');
    }
    input.addEventListener('input', (e) => renderMenu(e.target.value.trim()));
    input.addEventListener('blur', () => setTimeout(() => menu.classList.remove('is-open'), 150));
  }

  function setupAddressAutocomplete() {
    function attach() {
      const input = $('#xnParentAddress');
      if (!input || !(window.google && window.google.maps && window.google.maps.places)) return false;
      try {
        const ac = new window.google.maps.places.Autocomplete(input, {
          types: ['address'],
          componentRestrictions: { country: 'us' },
          fields: ['address_components', 'formatted_address'],
        });
        ac.addListener('place_changed', () => {
          const place = ac.getPlace();
          if (!place || !place.address_components) return;
          state.address.line1 = place.formatted_address || input.value;
          const zip = place.address_components.find((c) => c.types.includes('postal_code'));
          state.address.zip = zip ? zip.short_name : '';
          input.value = state.address.line1;
        });
        return true;
      } catch (e) { console.error(e); return false; }
    }
    if (!attach()) {
      const orig = window.initGoogleMapsCallback;
      window.initGoogleMapsCallback = function () {
        if (typeof orig === 'function') { try { orig(); } catch (e) { /* noop */ } }
        attach();
      };
    }
  }

  function validateStep3() {
    const fields = [
      { id: 'xnParentName', key: 'name', test: (v) => v.trim().split(/\s+/).length >= 2 },
      { id: 'xnParentAddress', key: 'line1', test: (v) => v.trim().length > 5 },
      { id: 'xnParentEmail', key: 'email', test: (v) => /^[^@]+@[^@]+\.[^@]+$/.test(v.trim()) },
      { id: 'xnParentPassword', key: 'password', test: (v) => v.length >= 6 },
      { id: 'xnParentPhone', key: 'phone', test: (v) => /^\d{10}$/.test(v.trim()) },
    ];
    let firstBad = null;
    fields.forEach((f) => {
      const el = document.getElementById(f.id);
      const ok = f.test(el?.value || '');
      el?.closest('[data-field]')?.classList.toggle('is-error', !ok);
      if (!ok && !firstBad) firstBad = el;
      if (ok) state.address[f.key] = (el?.value || '').trim();
    });
    state.address.apt = ($('#xnParentApt')?.value || '').trim();
    // SMS opt-in is optional (consent can't be a condition of purchase).
    state.address.smsOptIn = !!$('#xnSmsOptIn')?.checked;
    state.extras.breed = ($('#xnDogBreed')?.value || '').trim();
    state.extras.gender = (document.querySelector('input[name="dogGender"]:checked')?.value || '');
    state.extras.hearAboutUs = ($('#xnHearAboutUs')?.value || '');
    if (firstBad) firstBad.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return !firstBad;
  }

  async function submitActivate() {
    if (!validateStep3()) return;
    const btn = $('#xnActivateSubmit');
    if (btn) { btn.disabled = true; btn.classList.add('is-loading'); btn.querySelector('.xn-btn__label').textContent = 'Activating…'; }

    try {
      const fullAddress = [state.address.line1, state.address.apt].filter(Boolean).join(' ');
      const dogAge = state.pup.age;
      const date = new Date();
      const years = Math.floor(dogAge);
      const months = Math.round((dogAge - years) * 12);
      date.setFullYear(date.getFullYear() - years);
      date.setMonth(date.getMonth() - months);
      const estimatedBirthdate = date.toISOString().split('T')[0];

      const payload = {
        weight_lbs: state.pup.weight,
        breed: (state.extras.breed || '').toLowerCase(),
        estimatedBirthdate: estimatedBirthdate,
        dog_age: Math.round(dogAge),
        dog_age_float: dogAge,
        activity_level: null,
        activity_level_2: state.pup.activity,
        fixed_or_not: state.pup.fixed,
        weight_status: null,
        dog_name: (state.pup.name || '').toLowerCase(),
        dog_sex: state.extras.gender || null,
        zip_code: state.address.zip || null,
        parent_name: state.address.name.toLowerCase(),
        parent_address: fullAddress.toLowerCase(),
        pickup_location: null,
        parent_email: state.address.email.toLowerCase(),
        parent_phone_number: state.address.phone,
        sms_opt_in: !!state.address.smsOptIn,
        discount_code: state.discount.code || null,
        hear_about_us: state.extras.hearAboutUs || null,
        meal_plan_calories: state.adjustedCalories || state.caloriesWithTreats,
        recipe: state.recipe,
        dog_photo_url: state.extras.photoUrl || null,
        // Derived from the plan choice: Intro = new to fresh, Complete = already feeding fresh.
        feeds_fresh_food: state.selectedPlan === 'complete' ? 'yes'
          : state.selectedPlan === 'intro' ? 'no'
          : null,
      };

      // Fire the same calorie/lead email lambda as the legacy explore page (non-blocking).
      triggerCalorieEmailLambda();

      const res = await fetch(`${API_BASE}/store-customer-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      const body = typeof data.body === 'string' ? JSON.parse(data.body) : data;

      // Edge cases
      if (body && body.error === 'TOO_YOUNG') { showEdge('xnEdgeTooYoung'); return; }
      if (body && body.error === 'NOT_IN_AREA') { showEdge('xnEdgeNotInArea'); return; }
      if (!body || !body.sku_info) throw new Error(body && body.message || 'Order setup failed');

      state.skuInfo = body.sku_info;
      state.expectedDelivery = body.expected_delivery;
      state.shopifyStatus = body.shopify_status;

      clearPersistedForm(); // order set up successfully — drop the saved draft
      renderReview(body);
      $('#xnReview').classList.remove('is-hidden');
      scrollToEl('#xnReview');
      addXp(75, 'Plan activated!');
    } catch (err) {
      console.error(err);
      showEdge('xnEdgeGeneric');
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.classList.remove('is-loading');
        btn.querySelector('.xn-btn__label').textContent = 'Activate my pup\'s plan';
      }
    }
  }

  function showEdge(id) {
    ['xnEdgeTooYoung', 'xnEdgeNotInArea', 'xnEdgeGeneric'].forEach((eid) => {
      const el = document.getElementById(eid);
      if (el) el.classList.toggle('is-hidden', eid !== id);
    });
    const target = document.getElementById(id);
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // REVIEW + ADD TO CART
  // ─────────────────────────────────────────────────────────────────────────────
  function renderReview(body) {
    const planLabel = PLAN_LABELS[state.selectedPlan] || state.selectedPlan;
    const dailyPriceEl = document.getElementById(planPriceElId(state.selectedPlan));
    const rawPrice = parseFloat((dailyPriceEl?.dataset.original || '').toString()) ||
      parseFloat((dailyPriceEl?.textContent || '').replace(/[^\d.]/g, '')) || 0;
    const discounted = state.discount.percent ? rawPrice * (100 - state.discount.percent) / 100 : rawPrice;

    const recipeLabel = state.recipe === 'beef' ? 'Beef Bonanza' : state.recipe === 'mixed' ? 'Half Turkey / Half Beef' : 'Turkey Feast';
    $('#xnReviewRecipe').textContent = recipeLabel;
    $('#xnReviewCalories').textContent = `${state.adjustedCalories || state.caloriesWithTreats} cal/day`;
    $('#xnReviewPlan').textContent = planLabel;
    if (state.discount.percent > 0) {
      $('#xnReviewPerDay').innerHTML = `<span class="xn-price-strike">$${rawPrice.toFixed(2)}</span> $${discounted.toFixed(2)}`;
    } else {
      $('#xnReviewPerDay').textContent = `$${discounted.toFixed(2)}`;
    }
    $('#xnReviewAddress').textContent = [state.address.line1, state.address.apt].filter(Boolean).join(' ');
    $('#xnReviewDelivery').textContent = formatDate(body.expected_delivery);
  }

  function planPriceElId(plan) {
    return {
      complete: 'xnPriceComplete',
      intro: 'xnPriceIntro',
      'medium-topper': 'xnPriceMedium',
      'light-topper': 'xnPriceLight',
    }[plan] || 'xnPriceComplete';
  }

  function formatDate(d) {
    if (!d) return '—';
    try {
      // A bare "YYYY-MM-DD" (or with a midnight time) is parsed by JS as UTC midnight,
      // which renders as the *previous* day for US timezones. Parse the date parts as
      // local time so the arriving date matches what the backend actually means.
      let date;
      const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s]00:00(?::00)?(?:\.0+)?Z?)?$/);
      if (m) {
        date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      } else {
        date = new Date(d);
      }
      return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    } catch (e) { return d; }
  }

  function initReview() {
    $('#xnReviewBack')?.addEventListener('click', () => {
      $('#xnReview').classList.add('is-hidden');
      scrollToStep(3);
    });
    $('#xnReviewCart')?.addEventListener('click', addToCartAndRedirect);

    // When the user clicks "Go to cart" the button is disabled + spinner'd, then
    // we navigate away. Hitting Back restores this page from the bfcache with the
    // button frozen in that loading state — reset it whenever the page is shown.
    window.addEventListener('pageshow', () => {
      const btn = $('#xnReviewCart');
      if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
    });
  }

  async function addToCartAndRedirect() {
    if (!state.skuInfo) { showToast('No plan info yet'); return; }
    const btn = $('#xnReviewCart');
    if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }

    try {
      const sellingPlanId = pickSellingPlanId();
      const dogName = state.pup.name || 'your pup';
      const baseProps = {
        'Dog Name': dogName,
        'Calories': String(state.adjustedCalories || state.caloriesWithTreats),
        'Plan': PLAN_LABELS[state.selectedPlan] || state.selectedPlan,
      };

      let cartItems;
      const isMixed = state.recipe === 'mixed' && state.skuInfo.is_mixed;
      const planKey = state.selectedPlan;

      // Intro is the Complete plan shipped as a smaller 2-week wean-in. Add the
      // legacy checkout notes so the "first payment is intro-only" messaging is clear.
      const introProps = planKey === 'intro' ? {
        Details: 'This introductory order is designed to wean your dog onto fresh food from their current diet.',
        Important: "At checkout, if you see 'First payment $X, then $Y every 2 weeks,' the $Y is for the intro supply only.",
      } : {};

      if (isMixed) {
        // Quantities from legacy logic (intro = 10 total packs vs complete's 14)
        const qtyMap = {
          'complete': { t: 7, b: 7 },
          'intro': { t: 5, b: 5 },
          'medium-topper': { t: 4, b: 3 },
          'light-topper': { t: 2, b: 2 },
        }[planKey] || { t: 7, b: 7 };
        const turkeyVariant = pickVariant('turkey');
        const beefVariant = pickVariant('beef');
        cartItems = [
          { id: turkeyVariant, quantity: qtyMap.t, selling_plan: sellingPlanId, properties: { ...baseProps, ...introProps, Recipe: 'Turkey Feast (combo)' } },
          { id: beefVariant, quantity: qtyMap.b, selling_plan: sellingPlanId, properties: { ...baseProps, ...introProps, Recipe: 'Beef Bonanza (combo)' } },
        ];
      } else {
        const variantId = variantIdFromPackage(state.skuInfo.variant_id_package);
        // Pack counts per plan, matching the legacy explore page exactly:
        // complete = backend pack count (≈14), intro = 10, medium topper = 7, light topper = 4.
        const qty = {
          'complete': state.skuInfo.num_packs || 14,
          'intro': 10,
          'medium-topper': 7,
          'light-topper': 4,
        }[planKey] || (state.skuInfo.num_packs || 14);
        cartItems = [{ id: variantId, quantity: qty, selling_plan: sellingPlanId, properties: { ...baseProps, ...introProps } }];
      }

      // Guard: a missing/invalid variant id is the #1 cause of a silent empty cart.
      if (cartItems.some((it) => !it.id)) {
        console.error('Add to cart aborted — missing variant id', { cartItems, skuInfo: state.skuInfo });
        showToast('Something went wrong setting up your plan — please try again');
        if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
        return;
      }
      // Shopify rejects selling_plan: undefined/null; only send it when we have one.
      cartItems = cartItems.map((it) => {
        if (it.selling_plan == null) { const { selling_plan, ...rest } = it; return rest; }
        return it;
      });

      // Update order plan
      try {
        await fetch(`${API_BASE}/update-order`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
          body: JSON.stringify({
            email: state.address.email.toLowerCase(),
            update_fields: {
              selectedMealPlan: state.selectedPlan,
              dailyPrice: '0.00',
              totalPrice: '0.00',
            },
          }),
        });
      } catch (e) { /* non-blocking */ }

      await fetch('/cart/clear.js', { method: 'POST' });
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: cartItems }),
      });

      // Surface (instead of silently swallowing) an add failure — otherwise we'd
      // redirect to an empty cart and the user sees "Your cart is empty".
      if (!addRes.ok) {
        let errText = '';
        try { const e = await addRes.json(); errText = e.description || e.message || JSON.stringify(e); }
        catch (e) { errText = `HTTP ${addRes.status}`; }
        console.error('cart/add.js failed:', errText, { cartItems });
        throw new Error(errText || 'Add to cart failed');
      }

      // Confirm the cart actually has items before navigating away.
      try {
        const cartState = await (await fetch('/cart.js', { headers: { 'Accept': 'application/json' } })).json();
        // Diagnostic: log unit price × qty per line and the subtotal so we can
        // verify the math (Shopify prices are in cents).
        try {
          console.log('[explore cart] items:', (cartState.items || []).map((it) => ({
            title: it.title,
            qty: it.quantity,
            unit_price: (it.price / 100).toFixed(2),
            line_total: (it.line_price / 100).toFixed(2),
            selling_plan: it.selling_plan_allocation && it.selling_plan_allocation.selling_plan && it.selling_plan_allocation.selling_plan.name,
          })), 'subtotal:', (cartState.items_subtotal_price / 100).toFixed(2));
        } catch (e) { /* logging only */ }
        if (!cartState || !cartState.item_count) {
          throw new Error('Cart is still empty after add');
        }
      } catch (e) {
        console.error('Cart verification failed:', e);
        throw e;
      }

      const cfg = window.exploreNewConfig || {};
      if (cfg.isLoggedIn) {
        window.location.href = '/cart';
        return;
      }
      // Auto-create account using stored credentials
      if (state.shopifyStatus === 'AccountCreated') {
        window.location.href = '/account/login?return_url=/cart';
        return;
      }
      createAccountSilently();
    } catch (err) {
      console.error(err);
      showToast('Could not add to cart — try again');
      if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
    }
  }

  function pickVariant(recipe) {
    // Legacy structure: separate turkey/beef package objects for mixed orders.
    const pkg = recipe === 'turkey'
      ? state.skuInfo.variant_id_package_turkey || state.skuInfo.variant_id_package
      : state.skuInfo.variant_id_package_beef || state.skuInfo.variant_id_package;
    return variantIdFromPackage(pkg);
  }

  // The backend's variant_id_package is an OBJECT keyed by plan (not a bare id).
  // Pull the right variant id for the selected plan; complete + intro share the
  // complete-plan variant. Falls back to a scalar in case the shape ever changes.
  function planVariantKey() {
    const planKey = state.selectedPlan;
    if (planKey === 'medium-topper') return 'medium_topper_meal_plan_variant_id';
    if (planKey === 'light-topper') return 'light_topper_meal_plan_variant_id';
    return 'complete_meal_plan_variant_id';
  }

  function variantIdFromPackage(pkg) {
    if (!pkg) return null;
    if (typeof pkg === 'object') return pkg[planVariantKey()] || null;
    return pkg;
  }

  function pickSellingPlanId() {
    const planKey = state.selectedPlan;
    const pct = state.discount.percent;
    const groupKey = planKey === 'complete' || planKey === 'intro'
      ? 'complete'
      : planKey === 'medium-topper'
        ? 'medium'
        : 'light';
    if (pct && SELLING_PLANS_BY_DISCOUNT[pct]) {
      return SELLING_PLANS_BY_DISCOUNT[pct][groupKey] || DEFAULT_SELLING_PLANS[groupKey];
    }
    return DEFAULT_SELLING_PLANS[groupKey];
  }

  function createAccountSilently() {
    // Mirrors the legacy form-post path: POST to /account with hidden inputs
    const f = document.createElement('form');
    f.action = '/account';
    f.method = 'POST';
    f.style.display = 'none';
    const fields = {
      'form_type': 'create_customer',
      'utf8': '✓',
      'customer[email]': state.address.email,
      'customer[password]': state.address.password,
      'customer[first_name]': (state.address.name || '').split(' ')[0] || '',
      'customer[last_name]': (state.address.name || '').split(' ').slice(1).join(' ') || '',
      'return_to': '/cart',
    };
    Object.keys(fields).forEach((k) => {
      const inp = document.createElement('input');
      inp.type = 'hidden';
      inp.name = k;
      inp.value = fields[k];
      f.appendChild(inp);
    });
    try { sessionStorage.setItem('pendingReturnTo', '/cart'); } catch (e) { /* noop */ }
    document.body.appendChild(f);
    f.submit();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // HERO / NAV / EDGE FORMS
  // ─────────────────────────────────────────────────────────────────────────────
  function initHero() {
    $('#xnHeroStart')?.addEventListener('click', () => {
      if (state.step === 0) setStep(1);
      scrollToStep(1);
      $('#xnDogName')?.focus();
    });

    // Reveal the progress bar if the user scrolls past the hero without clicking Start.
    const step1El = $('#xnStep1');
    if (step1El && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && state.step === 0) {
            setStep(1);
            io.disconnect();
            break;
          }
        }
      }, { rootMargin: '0px 0px -40% 0px', threshold: 0.01 });
      io.observe(step1El);
    }

    $$('.xn-progress__step').forEach((b) => {
      b.addEventListener('click', () => {
        const s = parseInt(b.dataset.stepJump, 10);
        if (s <= state.step + 1) scrollToStep(s);
      });
    });

    // Edge form submissions (waiting list)
    ['xnTooYoungForm', 'xnNotInAreaForm'].forEach((id) => {
      const f = document.getElementById(id);
      if (!f) return;
      f.addEventListener('submit', async (e) => {
        e.preventDefault();
        const email = f.querySelector('input[type="email"]').value.trim();
        if (!/^[^@]+@[^@]+\.[^@]+$/.test(email)) { showToast('Valid email please'); return; }
        try {
          await fetch(`${API_BASE}/waiting-list`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
            body: JSON.stringify({ email, source: 'explore-new', reason: id }),
          });
          showToast("You're on the list! 🐾", { duration: 3000 });
        } catch (err) { showToast('Could not save — try again'); }
      });
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // HERO VIDEO — pause when offscreen, honor reduced-motion, enforce muted
  // ─────────────────────────────────────────────────────────────────────────────
  function initHeroVideo() {
    const video = $('.xn-hero__video');
    if (!video) return;

    // Enforce silent playback (some browsers ignore the muted attribute on autoplay)
    video.muted = true;
    video.defaultMuted = true;
    video.volume = 0;
    video.setAttribute('muted', '');

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      try { video.pause(); video.removeAttribute('autoplay'); } catch (e) { /* noop */ }
      return;
    }

    const tryPlay = () => { const p = video.play(); if (p && p.catch) p.catch(() => {}); };
    tryPlay();

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) tryPlay();
          else { try { video.pause(); } catch (e) { /* noop */ } }
        }
      }, { threshold: 0.1 });
      io.observe(video);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // BOOTSTRAP
  // ─────────────────────────────────────────────────────────────────────────────
  function init() {
    // Wait for lucide to load icons; fallback if it doesn't
    refreshIcons();
    document.addEventListener('DOMContentLoaded', refreshIcons);
    window.addEventListener('load', refreshIcons);

    initHero();
    initHeroVideo();
    initStep1();
    initStep2();
    initStep3();
    initReview();
    initDogPhotoUpload();
    // Animate the comparison bars when the collapsible is expanded (they're
    // hidden in a closed <details>, so the scroll observer may not catch them).
    const cmpToggle = document.getElementById('xnCompareToggle');
    cmpToggle?.addEventListener('toggle', () => {
      if (cmpToggle.open) document.getElementById('xnCompareBars')?.classList.add('is-inview');
    });
    renderCompareMatrix(); // seed bars with the snapshot until live pricing loads
    initScrollReveals();
    fetchActiveDiscountCodes(); // preload

    // Restore any previously-typed info, then start saving as the user types
    restorePersistedForm();
    initFormPersistence();

    // Re-run icon library after any dynamic DOM changes (cheap polling)
    setInterval(refreshIcons, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
